import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn, UserPlus, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth, formatApiError } from "@/auth/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
    else if (user) navigate("/area-paziente");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email });
        toast.success(t("auth.forgotSent"));
        setMode("login");
      } else if (mode === "login") {
        await login(email, password);
        toast.success(t("auth.welcome"));
      } else {
        await register(name, email, password);
        toast.success(t("auth.welcome"));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm bg-white";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="login-page">
      <Header />
      <main className="pt-40 sm:pt-60 pb-24 px-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
              {t("patientArea.label")}
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-3">
              {mode === "login" ? t("auth.loginTitle") : mode === "register" ? t("auth.registerTitle") : t("auth.forgotTitle")}
            </h1>
            {mode === "forgot" && (
              <p className="text-sm text-stone-500 mt-3">{t("auth.forgotText")}</p>
            )}
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-stone-100">
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="block text-sm text-stone-600 mb-2">{t("auth.name")}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className={inputClass}
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm text-stone-600 mb-2">{t("auth.email")}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClass}
                  data-testid="auth-email-input"
                />
              </div>
              {mode !== "forgot" && (
                <div>
                  <label className="block text-sm text-stone-600 mb-2">{t("auth.password")}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className={inputClass}
                    data-testid="auth-password-input"
                  />
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : mode === "login" ? (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    {t("auth.loginBtn")}
                  </>
                ) : mode === "register" ? (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    {t("auth.registerBtn")}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t("auth.sendLink")}
                  </>
                )}
              </Button>
            </form>

            {mode === "login" && (
              <button
                onClick={() => setMode("forgot")}
                className="w-full mt-4 text-sm text-stone-500 hover:text-[#2c4c3b] hover:underline"
                data-testid="forgot-password-link"
              >
                {t("auth.forgotPassword")}
              </button>
            )}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="w-full mt-3 text-sm text-[#2c4c3b] hover:underline"
              data-testid="auth-toggle-mode"
            >
              {mode === "login" ? t("auth.noAccount") : mode === "register" ? t("auth.haveAccount") : t("auth.backToLogin")}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
